import { getCustomerProjectSummary, listCustomerProjects } from '../api.js';
import { createQaChatPanel } from '../components/QaChatPanel.js';
import { customerProjectContext } from '../components/qa/context.js';
import { getCustomersProjectsCopy, getLanguage, getQaChatCopy } from '../i18n.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';

const html = String.raw;
const SUPPORTED_SUMMARY_LANGUAGES = new Set(['de', 'en', 'fr', 'es']);

const normalizeValue = (value) => String(value ?? '').trim();
const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toTimestamp = (value) => {
  const parsed = Date.parse(normalizeValue(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const interpolate = (template, values = {}) =>
  String(template ?? '').replace(/\{\{(\w+)\}\}/g, (_, key) => String(values[key] ?? ''));

const normalizeList = (value, limit = 5) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => normalizeValue(entry))
    .filter(Boolean)
    .slice(0, limit);

const normalizeSummary = (payload = {}) => ({
  whatItDoes: normalizeValue(payload.whatItDoes),
  whatItIsAbout: normalizeValue(payload.whatItIsAbout),
  nextSteps: normalizeList(payload.nextSteps, 5),
  highlights: normalizeList(payload.highlights, 5),
  sourceStats: {
    totalConversations: toNumber(payload?.sourceStats?.totalConversations, 0),
    summarizedConversations: toNumber(payload?.sourceStats?.summarizedConversations, 0),
    latestUpdatedAt: normalizeValue(payload?.sourceStats?.latestUpdatedAt) || '',
  },
  usedFallback: payload?.usedFallback === true,
});

const compareByLatest = (left, right) => toTimestamp(right?.latestUpdatedAt) - toTimestamp(left?.latestUpdatedAt);

const normalizeIndex = (customersPayload) =>
  (Array.isArray(customersPayload) ? customersPayload : [])
    .map((customer) => {
      const customerId = normalizeValue(customer?.customerId);
      if (!customerId) return null;

      const projects = (Array.isArray(customer?.projects) ? customer.projects : [])
        .map((project) => {
          const projectId = normalizeValue(project?.projectId);
          if (!projectId) return null;
          return {
            projectId,
            conversationCount: toNumber(project?.conversationCount, 0),
            latestUpdatedAt: normalizeValue(project?.latestUpdatedAt) || '',
          };
        })
        .filter(Boolean)
        .sort(compareByLatest);

      return {
        customerId,
        projectCount: toNumber(customer?.projectCount, projects.length),
        conversationCount: toNumber(customer?.conversationCount, 0),
        latestUpdatedAt: normalizeValue(customer?.latestUpdatedAt) || '',
        projects,
      };
    })
    .filter(Boolean)
    .sort(compareByLatest);

const formatDateTime = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '—';
  const parsed = Date.parse(normalized);
  if (!Number.isFinite(parsed)) return normalized;
  return new Date(parsed).toLocaleString();
};

const resolveSummaryLanguage = () => {
  const current = normalizeValue(getLanguage()).toLowerCase();
  const candidate = current.split('-')[0];
  return SUPPORTED_SUMMARY_LANGUAGES.has(candidate) ? candidate : 'de';
};

const buildSummaryCacheKey = (customerId, projectId, language) => {
  const normalizedCustomer = normalizeValue(customerId);
  const normalizedProject = normalizeValue(projectId);
  const normalizedLanguage = normalizeValue(language);
  if (!normalizedCustomer || !normalizedProject || !normalizedLanguage) {
    return '';
  }
  return `${normalizedCustomer}::${normalizedProject}::${normalizedLanguage}`;
};

const renderListItems = (items = []) => {
  if (!items.length) {
    return '<p class="muted small">—</p>';
  }
  return html`<ul class="customers-projects-summary-list">
    ${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
  </ul>`;
};

export const renderCustomersProjectsView = async (container) => {
  const copy = getCustomersProjectsCopy();
  const qaCopy = getQaChatCopy();

  const state = {
    customers: [],
    selectedCustomerId: '',
    selectedProjectId: '',
    summary: null,
    summaryKey: '',
    indexLoading: true,
    summaryLoading: false,
    indexError: '',
    summaryError: '',
  };

  const qaPanel = createQaChatPanel({
    copy: qaCopy,
    title: copy.qaTitle,
    description: copy.qaDescription,
    context: customerProjectContext(),
  });

  let destroyed = false;
  let indexRequestId = 0;
  let summaryRequestId = 0;
  const summaryCache = new Map();

  const getSelectedCustomer = () =>
    state.customers.find((customer) => customer.customerId === state.selectedCustomerId) ?? null;

  const getProjects = () => getSelectedCustomer()?.projects ?? [];

  const applyCachedSummaryForSelection = ({ customerId, projectId, language }) => {
    const cacheKey = buildSummaryCacheKey(customerId, projectId, language);
    if (!cacheKey) {
      state.summary = null;
      state.summaryKey = '';
      return false;
    }
    const cached = summaryCache.get(cacheKey);
    if (!cached) {
      state.summary = null;
      state.summaryKey = '';
      return false;
    }
    state.summary = cached;
    state.summaryKey = cacheKey;
    return true;
  };

  const syncQaPanel = () => {
    qaPanel.setContext(
      customerProjectContext({
        customerId: state.selectedCustomerId || undefined,
        projectId: state.selectedProjectId || undefined,
      }),
    );
    const qaHost = container.querySelector('[data-customers-projects-qa-host]');
    qaPanel.setHost(qaHost || null);
  };

  const render = () => {
    if (destroyed) return;

    const selectedCustomer = getSelectedCustomer();
    const projects = getProjects();
    const selectedProject = projects.find((project) => project.projectId === state.selectedProjectId) ?? null;
    const summaryLanguage = resolveSummaryLanguage();
    const activeSummaryKey = buildSummaryCacheKey(state.selectedCustomerId, state.selectedProjectId, summaryLanguage);
    const hasActiveSummary = Boolean(state.summary && state.summaryKey && state.summaryKey === activeSummaryKey);

    const renderMain = () => {
      if (state.indexLoading) {
        return html`
          <section class="card stack customers-projects-state-card">
            <p class="muted">${escapeHtml(copy.states.indexLoading)}</p>
          </section>
        `;
      }

      if (state.indexError) {
        return html`
          <section class="card stack customers-projects-state-card">
            <strong>${escapeHtml(copy.errors.indexTitle)}</strong>
            <p class="muted">${escapeHtml(state.indexError || copy.errors.indexDescription)}</p>
            <div class="row">
              <button type="button" class="ghost-button small" data-cp-action="retry-index">
                ${escapeHtml(copy.actions.retry)}
              </button>
            </div>
          </section>
        `;
      }

      if (!state.customers.length) {
        return html`
          <section class="card stack customers-projects-state-card">
            <strong>${escapeHtml(copy.states.emptyTitle)}</strong>
            <p class="muted">${escapeHtml(copy.states.emptyDescription)}</p>
            <div class="row">
              <a href="#/upload" class="button button-small">${escapeHtml(copy.actions.upload)}</a>
            </div>
          </section>
        `;
      }

      return html`
        <section class="customers-projects-layout">
          <article class="card stack customers-projects-pane">
            <div class="row customers-projects-pane-header">
              <h3>${escapeHtml(copy.panes.customers)}</h3>
            </div>
            <div class="customers-projects-list">
              ${state.customers
                .map((customer) => {
                  const active = state.selectedCustomerId === customer.customerId ? ' is-active' : '';
                  return html`
                    <button
                      type="button"
                      class="customers-projects-item${active}"
                      data-cp-customer="${escapeHtml(customer.customerId)}"
                    >
                      <strong>${escapeHtml(customer.customerId)}</strong>
                      <span class="muted small">
                        ${escapeHtml(String(customer.projectCount))} ${escapeHtml(copy.labels.projects)}
                        · ${escapeHtml(String(customer.conversationCount))} ${escapeHtml(copy.labels.conversations)}
                      </span>
                      <span class="muted tiny">
                        ${escapeHtml(copy.labels.lastUpdate)}: ${escapeHtml(formatDateTime(customer.latestUpdatedAt))}
                      </span>
                    </button>
                  `;
                })
                .join('')}
            </div>
          </article>

          <article class="card stack customers-projects-pane">
            <div class="row customers-projects-pane-header">
              <h3>${escapeHtml(copy.panes.projects)}</h3>
            </div>
            ${
              projects.length
                ? html`
                    <div class="customers-projects-list">
                      ${projects
                        .map((project) => {
                          const active = state.selectedProjectId === project.projectId ? ' is-active' : '';
                          return html`
                            <button
                              type="button"
                              class="customers-projects-item${active}"
                              data-cp-project="${escapeHtml(project.projectId)}"
                            >
                              <strong>${escapeHtml(project.projectId)}</strong>
                              <span class="muted small">
                                ${escapeHtml(String(project.conversationCount))} ${escapeHtml(copy.labels.conversations)}
                              </span>
                              <span class="muted tiny">
                                ${escapeHtml(copy.labels.lastUpdate)}: ${escapeHtml(formatDateTime(project.latestUpdatedAt))}
                              </span>
                            </button>
                          `;
                        })
                        .join('')}
                    </div>
                  `
                : `<p class="muted">${escapeHtml(copy.states.noProjects)}</p>`
            }
          </article>

          <div class="customers-projects-right stack">
            <article class="card stack customers-projects-pane customers-projects-detail">
              <div class="row customers-projects-pane-header">
                <h3>
                  ${escapeHtml(selectedCustomer?.customerId || '—')}
                  /
                  ${escapeHtml(selectedProject?.projectId || '—')}
                </h3>
                ${
                  hasActiveSummary && state.summary?.usedFallback
                    ? `<span class="pill customers-projects-fallback-pill">${escapeHtml(copy.labels.fallback)}</span>`
                    : ''
                }
              </div>

              ${
                !state.selectedProjectId
                  ? `<p class="muted">${escapeHtml(copy.states.selectProject)}</p>`
                  : state.summaryLoading && !hasActiveSummary
                    ? `<p class="muted">${escapeHtml(copy.states.summaryLoading)}</p>`
                    : state.summaryError && !hasActiveSummary
                      ? html`
                          <div class="stack tight">
                            <strong>${escapeHtml(copy.errors.summaryTitle)}</strong>
                            <p class="muted">${escapeHtml(state.summaryError || copy.errors.summaryDescription)}</p>
                            <div class="row">
                              <button type="button" class="ghost-button small" data-cp-action="retry-summary">
                                ${escapeHtml(copy.actions.retry)}
                              </button>
                            </div>
                          </div>
                        `
                      : html`
                          ${
                            state.summaryLoading
                              ? `<p class="muted tiny">${escapeHtml(copy.states.summaryChecking || copy.states.summaryLoading)}</p>`
                              : ''
                          }
                          ${
                            state.summaryError
                              ? html`
                                  <div class="stack tight">
                                    <strong>${escapeHtml(copy.errors.summaryTitle)}</strong>
                                    <p class="muted">${escapeHtml(state.summaryError || copy.errors.summaryDescription)}</p>
                                    <div class="row">
                                      <button type="button" class="ghost-button small" data-cp-action="retry-summary">
                                        ${escapeHtml(copy.actions.retry)}
                                      </button>
                                    </div>
                                  </div>
                                `
                              : ''
                          }
                          ${
                            hasActiveSummary
                              ? html`
                            <section class="stack tight">
                              <h4>${escapeHtml(copy.labels.whatItDoes)}</h4>
                              <p>${escapeHtml(state.summary.whatItDoes || '—')}</p>
                            </section>

                            <section class="stack tight">
                              <h4>${escapeHtml(copy.labels.whatItIsAbout)}</h4>
                              <p>${escapeHtml(state.summary.whatItIsAbout || '—')}</p>
                            </section>

                            <section class="stack tight">
                              <h4>${escapeHtml(copy.labels.nextSteps)}</h4>
                              ${renderListItems(state.summary.nextSteps)}
                            </section>

                            <section class="stack tight">
                              <h4>${escapeHtml(copy.labels.highlights)}</h4>
                              ${renderListItems(state.summary.highlights)}
                            </section>

                            <p class="muted tiny">
                              ${escapeHtml(
                                interpolate(copy.labels.sourceStats, {
                                  total: state.summary.sourceStats.totalConversations,
                                  used: state.summary.sourceStats.summarizedConversations,
                                }),
                              )}
                            </p>
                          `
                              : `<p class="muted">${escapeHtml(copy.states.noSummary)}</p>`
                          }
                        `
              }
            </article>

            <article class="card stack customers-projects-pane">
              ${
                state.selectedProjectId
                  ? '<div data-customers-projects-qa-host></div>'
                  : `<p class="muted">${escapeHtml(copy.states.selectProjectForQa)}</p>`
              }
            </article>
          </div>
        </section>
      `;
    };

    container.innerHTML = html`
      <div class="stack customers-projects-page">
        <section class="card stack customers-projects-header">
          <div class="row customers-projects-header-row">
            <div class="stack tight">
              <h2>${escapeHtml(copy.title)}</h2>
              <p class="muted">${escapeHtml(copy.description)}</p>
            </div>
            <button type="button" class="ghost-button small" data-cp-action="refresh" ${state.indexLoading ? 'disabled' : ''}>
              ${escapeHtml(copy.actions.refresh)}
            </button>
          </div>
        </section>

        ${renderMain()}
      </div>
    `;

    container.querySelector('[data-cp-action="refresh"]')?.addEventListener('click', () => {
      void loadIndex({ keepSelection: true });
    });

    container.querySelector('[data-cp-action="retry-index"]')?.addEventListener('click', () => {
      void loadIndex({ keepSelection: true });
    });

    container.querySelector('[data-cp-action="retry-summary"]')?.addEventListener('click', () => {
      if (state.selectedCustomerId && state.selectedProjectId) {
        void loadSummary(state.selectedCustomerId, state.selectedProjectId);
      }
    });

    container.querySelectorAll('[data-cp-customer]').forEach((button) => {
      button.addEventListener('click', () => {
        const customerId = normalizeValue(button.getAttribute('data-cp-customer'));
        if (!customerId || customerId === state.selectedCustomerId) return;
        const customer = state.customers.find((entry) => entry.customerId === customerId);
        if (!customer) return;
        summaryRequestId += 1;
        state.selectedCustomerId = customer.customerId;
        state.selectedProjectId = customer.projects[0]?.projectId ?? '';
        state.summaryError = '';
        state.summaryLoading = false;
        applyCachedSummaryForSelection({
          customerId: state.selectedCustomerId,
          projectId: state.selectedProjectId,
          language: resolveSummaryLanguage(),
        });
        render();
        if (state.selectedProjectId) {
          void loadSummary(state.selectedCustomerId, state.selectedProjectId);
        }
      });
    });

    container.querySelectorAll('[data-cp-project]').forEach((button) => {
      button.addEventListener('click', () => {
        const projectId = normalizeValue(button.getAttribute('data-cp-project'));
        if (!projectId || projectId === state.selectedProjectId || !state.selectedCustomerId) return;
        summaryRequestId += 1;
        state.selectedProjectId = projectId;
        state.summaryError = '';
        state.summaryLoading = false;
        applyCachedSummaryForSelection({
          customerId: state.selectedCustomerId,
          projectId: state.selectedProjectId,
          language: resolveSummaryLanguage(),
        });
        render();
        void loadSummary(state.selectedCustomerId, state.selectedProjectId);
      });
    });

    syncQaPanel();
  };

  const loadSummary = async (customerId, projectId) => {
    const normalizedCustomer = normalizeValue(customerId);
    const normalizedProject = normalizeValue(projectId);
    if (!normalizedCustomer || !normalizedProject) return;
    const language = resolveSummaryLanguage();
    const targetSummaryKey = buildSummaryCacheKey(normalizedCustomer, normalizedProject, language);

    const requestId = ++summaryRequestId;
    const cachedSummary = targetSummaryKey ? summaryCache.get(targetSummaryKey) : null;
    if (cachedSummary) {
      state.summary = cachedSummary;
      state.summaryKey = targetSummaryKey;
    } else if (state.summaryKey !== targetSummaryKey) {
      state.summary = null;
      state.summaryKey = '';
    }
    state.summaryLoading = true;
    state.summaryError = '';
    render();

    try {
      const payload = await getCustomerProjectSummary({
        customerId: normalizedCustomer,
        projectId: normalizedProject,
        language,
      });
      if (destroyed || requestId !== summaryRequestId) return;
      state.summary = normalizeSummary(payload);
      state.summaryKey = targetSummaryKey;
      if (targetSummaryKey) {
        summaryCache.set(targetSummaryKey, state.summary);
      }
      state.summaryError = '';
    } catch (error) {
      if (destroyed || requestId !== summaryRequestId) return;
      if (!state.summary || state.summaryKey !== targetSummaryKey) {
        state.summary = null;
        state.summaryKey = '';
      }
      state.summaryError = normalizeValue(error?.message) || copy.errors.summaryDescription;
    } finally {
      if (destroyed || requestId !== summaryRequestId) return;
      state.summaryLoading = false;
      render();
    }
  };

  const loadIndex = async ({ keepSelection = true } = {}) => {
    const requestId = ++indexRequestId;
    state.indexLoading = true;
    state.indexError = '';
    render();

    try {
      const payload = await listCustomerProjects();
      if (destroyed || requestId !== indexRequestId) return;

      state.customers = normalizeIndex(payload?.customers);

      if (!state.customers.length) {
        summaryRequestId += 1;
        state.selectedCustomerId = '';
        state.selectedProjectId = '';
        state.summary = null;
        state.summaryKey = '';
        state.summaryError = '';
        state.summaryLoading = false;
        state.indexLoading = false;
        render();
        return;
      }

      const selectedCustomer = keepSelection
        ? state.customers.find((entry) => entry.customerId === state.selectedCustomerId) ?? state.customers[0]
        : state.customers[0];

      const selectedProject = keepSelection
        ? selectedCustomer.projects.find((entry) => entry.projectId === state.selectedProjectId) ?? selectedCustomer.projects[0]
        : selectedCustomer.projects[0];

      state.selectedCustomerId = selectedCustomer.customerId;
      state.selectedProjectId = selectedProject?.projectId ?? '';
      state.summaryError = '';
      state.summaryLoading = false;
      applyCachedSummaryForSelection({
        customerId: state.selectedCustomerId,
        projectId: state.selectedProjectId,
        language: resolveSummaryLanguage(),
      });
      state.indexLoading = false;
      render();

      if (state.selectedCustomerId && state.selectedProjectId) {
        void loadSummary(state.selectedCustomerId, state.selectedProjectId);
      }
    } catch (error) {
      if (destroyed || requestId !== indexRequestId) return;
      summaryRequestId += 1;
      state.customers = [];
      state.selectedCustomerId = '';
      state.selectedProjectId = '';
      state.summary = null;
      state.summaryKey = '';
      state.summaryError = '';
      state.summaryLoading = false;
      state.indexError = normalizeValue(error?.message) || copy.errors.indexDescription;
      state.indexLoading = false;
      render();
    }
  };

  registerViewCleanup(() => {
    destroyed = true;
    indexRequestId += 1;
    summaryRequestId += 1;
    summaryCache.clear();
    qaPanel.destroy();
  });

  render();
  await loadIndex({ keepSelection: true });
};
